// Thin fetch wrapper around the Liminl NestJS backend (apps/api).
//
// Browser auth uses a host-only Secure HttpOnly cookie. JavaScript never reads
// or forwards the credential; `credentials: "include"` is the only auth hook.

const BASE_URL = (
  import.meta.env.VITE_LIMINL_API_BASE_URL?.trim() ||
  import.meta.env.VITE_API_URL?.trim() ||
  "http://localhost:4000"
).replace(/\/+$/, "");

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

type Json = Record<string, unknown> | unknown[] | null;

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "message" in parsed
        ? Array.isArray((parsed as { message: unknown }).message)
          ? ((parsed as { message: string[] }).message.join(", "))
          : String((parsed as { message: unknown }).message)
        : undefined) ?? res.statusText ?? `Request failed (${res.status})`;
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event("liminl:unauthorized"));
    }
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

function toNetworkApiError(error: unknown): ApiError {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : "Failed to fetch";
  if (name === "TimeoutError" || /timeout/i.test(message)) {
    return new ApiError(0, message, { code: 703 });
  }
  if (name === "AbortError") {
    return new ApiError(0, message, { code: 702 });
  }
  return new ApiError(0, message, { code: 701 });
}

async function request<T>(method: string, path: string, body?: Json): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;

  // Treat null and undefined identically: send no body and no Content-Type.
  // Express body-parser runs in strict mode and 400s on a JSON top-level
  // `null` ("Unexpected token 'n', \"null\" is not valid JSON"), so callers
  // like `httpClient.post('/foo')` (no body arg) MUST NOT serialize to "null".
  const hasBody = body !== undefined && body !== null;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (hasBody) headers["Content-Type"] = "application/json";

  try {
    const res = await fetch(url, {
      method,
      headers,
      credentials: "include",
      body: hasBody ? JSON.stringify(body) : undefined,
    });
    return parseResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toNetworkApiError(error);
  }
}

async function requestForm<T>(method: string, path: string, body: FormData): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;

  const headers: Record<string, string> = { Accept: "application/json" };

  try {
    const res = await fetch(url, {
      method,
      headers,
      credentials: "include",
      body,
    });
    return parseResponse<T>(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toNetworkApiError(error);
  }
}

export const httpClient = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: Json) => request<T>("POST", path, body ?? null),
  postForm: <T = unknown>(path: string, body: FormData) => requestForm<T>("POST", path, body),
  put: <T = unknown>(path: string, body?: Json) => request<T>("PUT", path, body ?? null),
  patch: <T = unknown>(path: string, body?: Json) => request<T>("PATCH", path, body ?? null),
  del: <T = unknown>(path: string) => request<T>("DELETE", path),
};

export type HttpClient = typeof httpClient;
